import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Tickets } from "@/src/gql/graphql";

export function useExportTickets() {
  return useMutation({
    mutationFn: async ({ tickets }: { tickets: Tickets[] }) => {
      if (!tickets || tickets.length === 0) {
        throw new Error("No ticket data to export");
      }

      const {
        objectsToCSV,
        downloadCSV,
        formatDateForCSV,
        formatCurrencyForCSV,
        formatNumberForCSV,
        formatArrayForCSV,
      } = await import("@/lib/utils/csv-export");

      const csvData = tickets.map((ticket) => ({
        "Ticket ID": ticket.id,
        "Ticket Number": ticket.ticket_number,
        Title: ticket.title,
        Description: ticket.description,
        Status: ticket.status,
        Priority: ticket.priority,
        Category: ticket.category,
        "Contact ID": ticket.contact_id,
        "Client Name": ticket.company_name || ticket.contact_name || "",
        "Assigned To": ticket.assigned_to || "",
        "Created By": ticket.created_by,
        "Estimated Hours": formatNumberForCSV(ticket.estimated_hours),
        "Actual Hours": formatNumberForCSV(ticket.actual_hours),
        "Billable Hours": formatNumberForCSV(ticket.billable_hours),
        "Non-Billable Hours": formatNumberForCSV(ticket.non_billable_hours),
        "Hourly Rate": formatCurrencyForCSV(ticket.hourly_rate),
        "Total Cost": formatCurrencyForCSV(ticket.total_cost),
        "Total Billed": formatCurrencyForCSV(ticket.total_billed),
        "Discount Amount": formatCurrencyForCSV(ticket.discount_amount),
        "Discount Type": ticket.discount_type,
        "Discount Percentage": formatNumberForCSV(ticket.discount_percentage),
        "SLA Hours": formatNumberForCSV(ticket.sla_hours),
        "Due Date": ticket.due_date ? formatDateForCSV(ticket.due_date) : "",
        "Resolved Date": ticket.resolved_at ? formatDateForCSV(ticket.resolved_at) : "",
        "Closed Date": ticket.closed_at ? formatDateForCSV(ticket.closed_at) : "",
        "Auto Assign": ticket.auto_assign ? "Yes" : "No",
        "Auto Escalate": ticket.auto_escalate ? "Yes" : "No",
        "Escalation Level": ticket.escalation_level,
        "Last Escalation": ticket.last_escalation_at
          ? formatDateForCSV(ticket.last_escalation_at)
          : "",
        "Reminder Sent": ticket.reminder_sent ? "Yes" : "No",
        "Reminder Sent Date": ticket.reminder_sent_at
          ? formatDateForCSV(ticket.reminder_sent_at)
          : "",
        Tags: formatArrayForCSV(ticket.tags),
        "Internal Notes": ticket.internal_notes || "",
        "Customer Visible Notes": ticket.customer_visible_notes || "",
        "Is Internal": ticket.is_internal ? "Yes" : "No",
        "Created Date": ticket.created_at ? formatDateForCSV(ticket.created_at) : "",
        "Updated Date": ticket.updated_at ? formatDateForCSV(ticket.updated_at) : "",
        "Last Activity": ticket.last_activity_at ? formatDateForCSV(ticket.last_activity_at) : "",
      }));

      const csvContent = objectsToCSV(csvData);
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `tickets_export_${timestamp}.csv`;

      downloadCSV(csvContent, filename);

      return { filename, recordCount: tickets.length };
    },
    onSuccess: (result) => {
      toast.success(`Exported ${result.recordCount} tickets to ${result.filename}`);
    },
    onError: (error) => {
      toast.error("Failed to export tickets");
      console.error("Error exporting tickets:", error);
    },
  });
}

export function useNavigateToTicketEdit() {
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ ticketId }: { ticketId: string }) => {
      router.push(`/tickets/${ticketId}`);
      return { success: true };
    },
    onError: (error) => {
      toast.error("Failed to navigate to ticket edit page");
      console.error("Error navigating to ticket edit:", error);
    },
  });
}
